import React, { useState, useEffect } from 'react';
import toast from "react-hot-toast";
import {
    Grid,
    Typography,
    IconButton,
    Button as MUButton,
    Box,
} from '@mui/material';
import { postRequestHeaders, trakrefFontFamily, trakrefFontFamilyBold } from '../../../utils';
import { Grey1, Grey3, trakrefBlue } from '../../../color_utils';
import ExitModal from '../../Common/CommonModal';
import { BreadCrumbs, DateInput, Input, LoadingBar, LoadingButtonV4, Select } from '../../Common';
import CloseIcon from '@mui/icons-material/Close';
import LocationDropdown from '../../AddAsset/LocationDropdown';
import Routes from '../../../routes';
import { GetAssetCodes } from '../../../CodesCache';
import { mapAndSortLocation } from '../../AddAsset/utils';
import UserInfo from '../../../UserInfo';
import Validator from '../../../Utils/Validator';
import { useHistory } from 'react-router-dom';
import useNavigate from '../../../hooks/useNavigate';
import useBreadCrumbs from '../../../hooks/useBreadCrumbs';

const RecordApplianceDisposals = ({
    match: {
        params: { id: applianceDisposalID = '' },
    }
}) => {
    const history = useHistory();
    const pageHeader = applianceDisposalID > 0 ? "Edit Appliance Disposal" : "Record Appliance Disposal";
    const maxWeightLbs = 15;
    const maxWeightKgs = 6.80389;
    const minWeight = 0;

    const [loading, setLoading] = useState(true);
    const [locations, setLocations] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [ErrorObject, setErrorObject] = useState({});
    const [isProcessingSubmission, setIsProcessingSubmission] = useState(false);
    const [exitModal, setExitModal] = useState(false);

    // ------ Appliance Disposals Field ----- \\
    const [name, setName] = useState("");
    const [location, setLocation] = useState("");
    const [materialType, setMaterialType] = useState("");
    const [shutdownDate, setShutdownDate] = useState("");
    const [capacity, setCapacity] = useState("");
    const [amountRecovered, setAmountRecovered] = useState("");
    const [amountReclamation, setAmountReclamation] = useState("");
    const [notes, setNotes] = useState("");

    const navigate = useNavigate({
        pageName: 'RecordApplianceDisposals'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'RecordApplianceDisposals', text: `${applianceDisposalID ? 'Edit' : 'Record'} Appliance Disposal`, link: history.location.pathname }
    });

    const badValues = [undefined, null, ""];
    const weightUnit = UserInfo.getWeightUnits();
    const maxWeightDisplay = weightUnit === 'Lbs' ? maxWeightLbs : maxWeightKgs;

    const capacityHelperText = `Capacity must be less than ${maxWeightDisplay} ${weightUnit} and greater than ${minWeight} ${weightUnit}`;
    const amountHelperText = `Amount must be less than ${maxWeightDisplay} ${weightUnit} and not be less than ${minWeight} ${weightUnit}`;
    const notesHelperText = "Enter a minimum of 120 characters";
    const requiredErrorText = "This field is required";
    const recordApplianceText = "The appliance disposal is successfully recorded.";
    const editApplianceText = "The record has been successfully edited.";
    const allowMin = true;

    const GetAssetDropdownCodes = async function () {
        const response = await fetch(Routes.Asset.AddAssetDropdowns());
        const data = await response.json();

        return data;
    };
    const computeClass = (obj) => {
        if (obj.enabled === true || obj.name.toLowerCase() === "r-404" || obj.name.toLowerCase() === "r-410") {
            if (obj.class === 'Class FR') {
                return obj.class;
            } else {
                return "";
            }
        } else {
            return "Bad";
        }
    };

    const validateFields = () => {
        const errorObject = {};
        let isValid = true;

        const isShutdownDateValid = Validator.CheckDate(shutdownDate);


        if (!name) {
            errorObject.name = true;
        }

        if (!location) {
            errorObject.location = true;
        }

        if (!materialType) {
            errorObject.materialType = true;
        }

        if (!shutdownDate || !isShutdownDateValid) {
            errorObject.shutdownDate = requiredErrorText;
        }

        if (!capacity) {
            errorObject.capacity = requiredErrorText;
        } else {
            const isCapacityValid = Validator.CheckValidWeightNoMetrics(
                capacity,
                minWeight,
                maxWeightDisplay,
            );

            if (!isCapacityValid) {
                errorObject.capacity = capacityHelperText;
            }
        }

        if (badValues.includes(amountRecovered)) {
            errorObject.amountRecovered = requiredErrorText;
        } else {
            const isAmountRecoveredValid = Validator.CheckValidWeightNoMetrics(
                amountRecovered,
                minWeight,
                maxWeightDisplay,
                allowMin
            );

            if (!isAmountRecoveredValid) {
                errorObject.amountRecovered = amountHelperText;
            }
        }

        if (badValues.includes(amountReclamation)) {
            errorObject.amountReclamation = requiredErrorText;
        } else {
            const isAmountReclamationValid = Validator.CheckValidWeightNoMetrics(
                amountReclamation,
                minWeight,
                maxWeightDisplay,
                allowMin
            );

            if (!isAmountReclamationValid) {
                errorObject.amountReclamation = amountHelperText;
            }
        }

        if (!notes) {
            errorObject.notes = requiredErrorText;
        }

        if (notes && notes.length < 120) {
            errorObject.notes = notesHelperText;
        }

        setErrorObject(errorObject);
        if (Object.values(errorObject).some(state => state == true || typeof state == 'string')) {
            isValid = false;
        }
        console.log('errors for record appliance disposals', errorObject);
        return isValid;
    };

    const mapFromApplianceDisposal = () => {
        return {
            id: applianceDisposalID,
            name,
            locationId: location,
            materialTypeId: materialType,
            shutdownDate,
            capacity,
            amountRecovered,
            amountReclamation,
            notes,
        };
    };

    const submit = async () => {
        setIsProcessingSubmission(true);
        const isValid = validateFields();
        console.log('all fields valid', isValid);

        if (isValid === true) {
            const response = await fetch(Routes.Asset.UpdateApplianceDisposal(), {
                method: 'POST',
                headers: { ...postRequestHeaders },
                body: JSON.stringify(mapFromApplianceDisposal())
            });

            if (response.status === 200) {
                toast.success(applianceDisposalID ? editApplianceText: recordApplianceText, { duration: 6000 });
                navigate.goBack('/ApplianceDisposals');
            } else {
                toast.error("There was an error saving the appliance disposal", { duration: 6000 });
            }
        }

        setIsProcessingSubmission(false);
    };

    const fetchApplianceDisposal = async () => {
        try {
            const response = await fetch(Routes.Asset.GetApplianceDisposal(applianceDisposalID));
            const data = await response.json();

            if (data.errors) {
                console.log(data.error);
                setLoading(false);
                return;
            }

            setName(data.name);
            setLocation(data.locationId);
            setShutdownDate(data.shutdownDate);
            setMaterialType(data.materialTypeId);
            setCapacity(data.capacity);
            setAmountReclamation(data.amountReclamation);
            setAmountRecovered(data.amountRecovered);
            setNotes(data.notes);
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    };

    const fetchData = async () => {
        const data = await GetAssetDropdownCodes();
        const cachedata = await GetAssetCodes();
        const sortedLocations = mapAndSortLocation(data.locations);
        const MaterialTypesFiltered = cachedata.materialTypes.filter((obj) => obj.accountTypeId != 16 && (obj.id == 82 || computeClass(obj) == ''));
        setLocations(sortedLocations);
        setMaterialTypes(MaterialTypesFiltered);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        fetchApplianceDisposal();
    }, []);

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: "row",
                    maxWidth: "calc(68% + 26px)",
                    marginBottom: "26px"
                }}
            >
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexDirection: "row",
                    }}
                >
                    <IconButton aria-label="close" onClick={() => setExitModal(true)}>
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        variant="h5"
                        sx={{ "&.MuiTypography-root": { fontFamily: trakrefFontFamily } }}
                        style={{ color: Grey1 }}
                    >
                        {pageHeader}
                    </Typography>
                </Box>
                <Grid item ml={'auto'}>
                    <LoadingButtonV4
                        buttonText={`${applianceDisposalID ? 'SAVE' : 'RECORD'}`}
                        loading={loading || isProcessingSubmission}
                        onClick={() => submit()}
                    />
                </Grid>
            </Box>

            <Grid
                container
                spacing={2}
                sx={{
                    maxWidth: "68%",
                    ml: "26px",
                }}
            >
                <Grid item xs={12}>
                    <Grid container columnSpacing={6} rowSpacing={3} sx={{ marginBottom: "20px" }}>
                        <Grid item xs={6} lg={6}>
                            <Input
                                required
                                fullWidth
                                label="Appliance Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                error={ErrorObject.name}
                            />
                        </Grid>

                        <Grid item xs={6} lg={6}>
                            <Input
                                required
                                alwaysShowHelper
                                fullWidth
                                type="number"
                                label={`Appliance Capacity (${UserInfo.weightUnits})`}
                                value={capacity}
                                onChange={e => setCapacity(e.target.value)}
                                error={ErrorObject.capacity}
                                helperText={ErrorObject.capacity || capacityHelperText}
                            />
                        </Grid>

                        <LocationDropdown
                            required
                            locations={locations}
                            location={location}
                            onChangeLocation={setLocation}
                            ErrorObject={ErrorObject.location}
                        />

                        <Grid item xs={6} lg={6}>
                            <Input
                                required
                                alwaysShowHelper
                                fullWidth
                                type="number"
                                label={`Amount Recovered (${UserInfo.weightUnits})`}
                                value={amountRecovered}
                                onChange={e => setAmountRecovered(e.target.value)}
                                error={ErrorObject.amountRecovered}
                                helperText={ErrorObject.amountRecovered || amountHelperText}
                            />
                        </Grid>

                        <Grid item xs={6} lg={6}>
                            <Select
                                required
                                value={materialType}
                                label={`Refrigerant Type`}
                                options={materialTypes}
                                onChange={e => setMaterialType(e.target.value)}
                                error={ErrorObject.materialType}
                            />
                        </Grid>

                        <Grid item xs={6} lg={6}>
                            <Input
                                required
                                alwaysShowHelper
                                fullWidth
                                type="number"
                                label={`Amount send to reclamation (${UserInfo.weightUnits})`}
                                value={amountReclamation}
                                onChange={e => setAmountReclamation(e.target.value)}
                                error={ErrorObject.amountReclamation}
                                helperText={ErrorObject.amountReclamation || amountHelperText}
                            />
                        </Grid>
                        <Grid item xs={6} lg={6}>
                            <DateInput
                                allowPast
                                date={shutdownDate}
                                onChange={date => setShutdownDate(date)}
                                label="Shutdown Date *"
                                error={ErrorObject.shutdownDate}
                                customHelperText={ErrorObject.shutdownDate}
                            />
                        </Grid>
                        <Grid item xs={6} lg={6}>
                            <Input
                                required
                                alwaysShowHelper
                                fullWidth
                                label="Notes"
                                value={notes}
                                maxCharacterLength={255}
                                onChange={e => setNotes(e.target.value)}
                                error={ErrorObject.notes}
                                helperText={ErrorObject.notes || notesHelperText}
                                secondaryHelperText={`${notes.length}/120`}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <ExitModal
                open={exitModal === true}
                close={() => setExitModal(false)}
                bottom={20}
            >
                <Grid container>
                    <Grid item xs={12} lg={12} align='center' sx={{ margin: '6% 0% 1%' }}>
                        <Typography
                            variant="h6"
                            sx={{ '&.MuiTypography-root': { fontSize: '1.375rem', color: Grey1 } }}
                            align='center'
                        >
                            Are you sure you want to leave this page?
                        </Typography>
                    </Grid>
                    <Grid item xs={12} lg={12} align='center' sx={{ marginBottom: '8%' }}>
                        <Typography
                            variant="paragraph"
                            
                            style={{ color: Grey3 }}
                            align='center'
                        >
                            Any new data you have entered won't be saved
                        </Typography>
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <MUButton
                            fullWidth
                            variant='contained'
                            onClick={() => history.goBack()}
                            style={{
                                backgroundColor: trakrefBlue,
                                marginBottom: '3%',
                                width: '65%'
                            }}
                        >
                            <Typography
                                variant='h6'
                                sx={{
                                    padding: '2% 0%',
                                    color: '#fff',
                                    fontSize: '16px',
                                    '&.MuiTypography-root': { fontFamily: trakrefFontFamilyBold }
                                }}
                            >
                                Leave this page
                            </Typography>
                        </MUButton>
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <MUButton
                            variant='outlined'
                            fullWidth
                            onClick={() => setExitModal(false)}
                            style={{ width: '65%', marginBottom: '5%', borderColor: trakrefBlue }}
                        >
                            <Typography
                                variant='h6'
                                sx={{
                                    padding: '2% 0%',
                                    color: trakrefBlue,
                                    fontSize: '16px',
                                    '&.MuiTypography-root': { fontFamily: trakrefFontFamilyBold }
                                }}
                            >
                                Stay on this page
                            </Typography>
                        </MUButton>
                    </Grid>
                </Grid>
            </ExitModal>
        </>
    );
};

export default RecordApplianceDisposals;
